import React, { useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { formatDateTimeInChicago } from "../../../helpers/formatingFunctions";
import PreviewGalleryModal from "../Accident/PreviewGalleryModal/PreviewGalleryModal";

interface TrailerAccidentsTableProps {
  trailerId: string;
}

const fetchAccidentsByTrailer = async (
  token: string | null | undefined,
  trailerId: string
) => {
  const { data } = await axios.get(
    `${apiVariable}/accidents/byTrailer/${trailerId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

const TrailerAccidentsTable: React.FC<TrailerAccidentsTableProps> = ({
  trailerId,
}) => {
  const { token } = useAuth();
  const [selectedAccident, setSelectedAccident] = useState<any | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const {
    data: accidents,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["trailerAccidents", trailerId],
    queryFn: () => fetchAccidentsByTrailer(token, trailerId),
    enabled: !!trailerId && !!token,
  });

  const columns: GridColDef[] = [
    {
      field: "date",
      headerName: "Date & Time",
      width: 210,
      type: "dateTime",
      valueFormatter: (params) => formatDateTimeInChicago(params),
    },
    { field: "typeOfIncident", headerName: "Type", width: 180 },
    { field: "description", headerName: "Description", width: 300 },
    { field: "location", headerName: "Location", width: 200 },
    { field: "claimNumber", headerName: "Claim #", width: 150 },
    {
      field: "claimAmount",
      headerName: "Claim Amount",
      width: 150,
      valueFormatter: (p) => (p != null ? `$${Number(p).toFixed(2)}` : ""),
    },
    {
      field: "preview",
      headerName: "Files",
      sortable: false,
      width: 120,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setSelectedAccident(params.row);
            setPreviewOpen(true);
          }}
        >
          Preview Files
        </Button>
      ),
    },
  ];

  if (isLoading) return <CircularProgress />;
  if (isError)
    return (
      <Typography color="error">Error: {(error as Error).message}</Typography>
    );

  return (
    <Box sx={{ height: 400, width: "80%" }}>
      <DataGrid
        rows={accidents ?? []}
        columns={columns}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
      />

      {selectedAccident && (
        <PreviewGalleryModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          accident={selectedAccident}
        />
      )}
    </Box>
  );
};

export default TrailerAccidentsTable;
